import React, { useState, useCallback } from 'react';
import Modal from '../Common/Modal';
import ConfigForm from './ConfigForm';
import VisualMapper from './VisualMapper';
import ReviewSubmit from './ReviewSubmit';

const STEPS = [
  { key: 'config', label: 'Upload & Configure', icon: '1' },
  { key: 'mapper', label: 'Place Signatures', icon: '2' },
  { key: 'review', label: 'Review & Send', icon: '3' },
];

const INITIAL_STATE = {
  // Document
  file: null,
  fileBase64: '',
  fileName: '',

  // Signature type: 'aadhaar_single' | 'aadhaar_multi' | 'digisign'
  signatureType: 'aadhaar_single',

  // Signers array
  signers: [
    { name: '', email: '', mobile: '', sequence: 1 },
  ],

  // Options
  reason: 'Signing',
  daysToExpire: 7,
  needNameMatch: false,
  percentageNameMatch: 80,
  redirectUrl: '',

  // DigiSign-specific
  signatureMode: 'email',       // 'email' | 'signing_url'
  signatureFlow: '1',           // '1' = Parallel, '2' = Sequential

  // Step 2 output — signature placements
  signatureCoordinates: [],
};

const NewRequestModal = ({ isOpen, onClose, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_STATE);

  const resetAndClose = useCallback(() => {
    setActiveStep(0);
    setFormData(INITIAL_STATE);
    onClose();
  }, [onClose]);

  const handleConfigComplete = useCallback((configData) => {
    setFormData((prev) => ({ ...prev, ...configData }));
    setActiveStep(1);
  }, []);

  const handleMapperComplete = useCallback((coordinates) => {
    setFormData((prev) => ({ ...prev, signatureCoordinates: coordinates }));
    setActiveStep(2);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={resetAndClose} title="New eSign Request">
      {/* Stepper */}
      <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
        <div className="flex items-center justify-between">
          {STEPS.map((step, i) => (
            <React.Fragment key={step.key}>
              <div className="flex items-center gap-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    i < activeStep
                      ? 'bg-indigo-600 text-white'
                      : i === activeStep
                      ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-600'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {i < activeStep ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    step.icon
                  )}
                </div>
                <span
                  className={`text-sm font-medium hidden sm:inline ${
                    i <= activeStep ? 'text-gray-900' : 'text-gray-400'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-3 rounded ${
                    i < activeStep ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Body — scrollable */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeStep === 0 && (
          <ConfigForm
            initialData={formData}
            onComplete={handleConfigComplete}
          />
        )}
        {activeStep === 1 && (
          <VisualMapper
            formData={formData}
            onComplete={handleMapperComplete}
          />
        )}
        {activeStep === 2 && (
          <ReviewSubmit
            formData={formData}
            onSuccess={onSuccess}
            onClose={resetAndClose}
          />
        )}
      </div>

      {/* Footer — only show Back on steps > 0 */}
      {activeStep > 0 && (
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center flex-shrink-0">
          <button
            onClick={handleBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
      )}
    </Modal>
  );
};

export default NewRequestModal;
